import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import FormActionButtons from "../Assets/FormActionButtons";
import LanguageTabs, { FormLang } from "../Assets/LanguageTabs";
import { useTranslation } from "react-i18next";

interface HistoryEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (id: string, formData: { text: { en?: string; mk?: string; sq?: string } }) => Promise<void>;
  initialData: {
    text: { en: string; mk: string; sq?: string };
    _id: string;
  };
  lang: string;
}

type Multi = { en: string; mk: string; sq: string };

const HistoryEditModal: React.FC<HistoryEditModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
}) => {
  const { t } = useTranslation();
  const [activeLang, setActiveLang] = useState<FormLang>("en");
  const [text, setText] = useState<Multi>({
    en: initialData.text.en || "",
    mk: initialData.text.mk || "",
    sq: initialData.text.sq || "",
  });

  useEffect(() => {
    setText({
      en: initialData.text.en || "",
      mk: initialData.text.mk || "",
      sq: initialData.text.sq || "",
    });
  }, [initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!isValidText(text.en) || !isValidText(text.mk)) {
      alert('Text is required in EN and MK.');
      return;
    }

    onSubmit(initialData._id, { text });
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const isValidText = (htmlContent: string) => {
    if (!htmlContent) return false;
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;
    const textContent = tempDiv.textContent || "";
    const hasText = textContent.trim().length > 0;
    const hasNonTextContent = tempDiv.querySelector("img, video, iframe") !== null;
    return hasText || hasNonTextContent;
  };

  const isUpdateDisabled = !isValidText(text.en) || !isValidText(text.mk);

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'link'
  ];

  if (!isOpen) return null;

  const langSuffix = activeLang.toUpperCase();

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-3xl relative max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4 text-center">
          {t(`forms.editHistory`)}
        </h2>

        <LanguageTabs active={activeLang} onChange={setActiveLang} />

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block font-medium mb-1">{t(`forms.text`)} ({langSuffix})</label>
            <ReactQuill
              key={`text-${activeLang}`}
              value={text[activeLang]}
              onChange={(value) => setText((prev) => ({ ...prev, [activeLang]: value }))}
              theme="snow"
              className="h-64"
              modules={quillModules}
              formats={quillFormats}
            />
          </div>
          <br></br>
          <br></br>
          <FormActionButtons
            edit={true}
            onClose={handleCancel}
            onSubmit={handleSubmit}
            disabled={isUpdateDisabled}
          />
        </form>
      </div>
    </div>
  );
};

export default HistoryEditModal;
